import { Flex } from '@mantine/core';
import { useEffect, useMemo, useRef } from 'react';
import { StepCard } from 'src/UI/generate-flow/StepCard';
import { ClientFamilyToggle } from 'src/UI/generate-flow/ClientFamilyToggle';
import { ClientList, ClientListItem, FamilyList } from 'src/UI/generate-flow/ClientList';
import { filterAndOrderFamilies } from 'src/UI/generate-flow/familyList';
import { useClients } from 'src/clients/hooks/useClients';
import { useFamilies } from 'src/clients/hooks/useFamilies';
import { DataExportStepProps } from '../types';
import { Family } from 'src/clients/hooks/useFamily';
import { matchesClientSearch } from 'src/utils/clientSearch';

type Props = DataExportStepProps & {
  preselectClientId?: string;
  preselectFamilyId?: string;
};

export function DataExportClientStep({
  data,
  setData,
  filter,
  searchBar,
  preselectClientId,
  preselectFamilyId,
}: Props) {
  const { clients } = useClients();
  const { families } = useFamilies();

  const term = (filter ?? '').trim().toLowerCase();

  const clientItems: ClientListItem[] = useMemo(() => {
    const base: ClientListItem[] = clients.map(c => ({ client: c }));

    base.sort((a, b) => {
      if (preselectClientId) {
        if (a.client.id === preselectClientId) return -1;
        if (b.client.id === preselectClientId) return 1;
      }
      return a.client.lastName.localeCompare(b.client.lastName);
    });

    if (!term) return base;
    return base.filter(({ client }) =>
      matchesClientSearch([client.firstName, client.lastName, client.emailAddress], term),
    );
  }, [clients, term, preselectClientId]);

  const filteredFamilies = useMemo(
    () => filterAndOrderFamilies(families, term, preselectFamilyId),
    [families, term, preselectFamilyId],
  );

  useEffect(() => {
    if (preselectFamilyId) return;
    if (data.isFamily) return;
    if (data.client?.id) return;
    if (!preselectClientId) return;

    const match = clients.find(c => c.id === preselectClientId);
    if (!match) return;

    setData(prev => {
      if (prev.isFamily) return prev;
      return { ...prev, client: match };
    });
  }, [clients, data.isFamily, data.client?.id, preselectClientId, preselectFamilyId, setData]);

  const currentFamilyId = data.isFamily ? data.family?.id : undefined;

  // Apply the family preselection only once, otherwise toggling to "Clients"
  // (which clears the family) would immediately re-select the family and make
  // the Clients tab impossible to choose.
  const familyPreselectApplied = useRef(false);
  useEffect(() => {
    if (!preselectFamilyId) return;
    if (familyPreselectApplied.current) return;
    if (currentFamilyId) return;

    const match = families.find(f => f.id === preselectFamilyId);
    if (!match) return;

    familyPreselectApplied.current = true;
    setData(prev => {
      if (prev.isFamily && prev.family?.id) return prev;
      return {
        ...prev,
        isFamily: true,
        client: undefined,
        family: match,
        selectedMemberClientIds: undefined,
      };
    });
  }, [families, currentFamilyId, preselectFamilyId, setData]);

  const title = data.isFamily
    ? 'Select the family to generate data export'
    : 'Select the client to generate data export';

  return (
    <Flex direction="column" h="100%" mih={0}>
      <StepCard title={title} searchBar={searchBar}>
        <ClientFamilyToggle
          isFamily={data.isFamily}
          onToggle={isFamily => {
            setData(prev => {
              if (isFamily === prev.isFamily) return prev;
              return isFamily
                ? {
                    ...prev,
                    isFamily: true,
                    family: undefined,
                    client: undefined,
                    selectedMemberClientIds: undefined,
                  }
                : {
                    ...prev,
                    isFamily: false,
                    client: undefined,
                    family: undefined,
                    selectedMemberClientIds: undefined,
                  };
            });
          }}
        />
        {data.isFamily ? (
          <FamilyList
            items={filteredFamilies}
            selectedFamilyId={data.family?.id}
            onSelect={(family: Family) => {
              setData(prev => {
                if (!prev.isFamily) return prev;
                if (prev.family?.id === family.id) return prev;
                return { ...prev, family, selectedMemberClientIds: undefined };
              });
            }}
          />
        ) : (
          <ClientList
            items={clientItems}
            selectedClientId={data.client?.id}
            onSelect={client => {
              setData(prev => {
                if (prev.isFamily) return prev;
                return { ...prev, client };
              });
            }}
          />
        )}
      </StepCard>
    </Flex>
  );
}
